import React, { useState, useEffect } from 'react';
import { 
    getPendingUsers, 
    approveUser, 
    getFemaleUsers, 
    updateUserPrice,
    getStatistics,
    getAllUsers,
    addUserBalance,
    deleteUser
} from '../utils/api';
import { notify } from '../utils/notify';

function AdminDashboard() {
    const [activeTab, setActiveTab] = useState('pending');
    const [pendingUsers, setPendingUsers] = useState([]);
    const [approvedUsers, setApprovedUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [statistics, setStatistics] = useState(null);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const loadData = async () => {
        try {
            if (activeTab === 'pending') {
                const data = await getPendingUsers();
                setPendingUsers(data);
            } else if (activeTab === 'approved') {
                const data = await getFemaleUsers();
                setApprovedUsers(data);
            } else if (activeTab === 'all-users') {
                const data = await getAllUsers();
                setAllUsers(data);
            } else if (activeTab === 'statistics') {
                const data = await getStatistics();
                setStatistics(data);
            }
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    const handleApprove = async (userId, price) => {
        try {
            await approveUser(userId, 'approved', parseFloat(price));
            loadData();
        } catch (error) {
            console.error('Error approving user:', error);
        }
    };

    const handleReject = async (userId) => {
        try {
            await approveUser(userId, 'rejected');
            loadData();
        } catch (error) {
            console.error('Error rejecting user:', error);
        }
    };

    const handleUpdatePrice = async (userId, newPrice) => {
        try {
            await updateUserPrice(userId, parseFloat(newPrice));
            notify.success('Success', 'Price updated.');
            loadData();
        } catch (error) {
            console.error('Error updating price:', error);
            await notify.error('Update failed', error.response?.data?.message || error.message);
        }
    };

    const handleAddBalance = async (userId, minutes) => {
        try {
            if (!minutes || minutes <= 0) {
                await notify.warning('Invalid amount', 'Please enter a valid number of minutes (greater than 0).');
                return;
            }
            // Convert minutes to seconds (balance is stored in seconds)
            const balanceInSeconds = Math.floor(minutes * 60);
            await addUserBalance(userId, balanceInSeconds);
            notify.success('Success', `Added ${minutes} minutes to user balance.`);
            loadData();
        } catch (error) {
            console.error('Error adding balance:', error);
            await notify.error('Add balance failed', error.response?.data?.message || error.message);
        }
    };

    const handleDeleteUser = async (userId, userName) => {
        console.log('🗑️ Delete button clicked:', { userId, userName });

        const confirmed = await notify.confirm({
            title: 'Delete user?',
            text: `Permanently delete "${userName}"? This cannot be undone and will delete photos too.`,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            icon: 'warning'
        });
        if (!confirmed) {
            console.log('❌ User cancelled deletion');
            return;
        }
        
        console.log('✅ User confirmed deletion, calling API...');
        
        try {
            console.log('📞 Calling deleteUser API with userId:', userId);
            const result = await deleteUser(userId);
            console.log('✅ Delete successful:', result);
            notify.success('Deleted', 'User deleted successfully.');
            loadData();
        } catch (error) {
            console.error('❌ Error deleting user:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            await notify.error('Delete failed', error.response?.data?.message || error.message);
        }
    };

    return (
        <div className="admin-dashboard">
            <header>
                <h1>Admin Panel</h1>
            </header>

            <div className="tabs">
                <button 
                    className={activeTab === 'pending' ? 'active' : ''}
                    onClick={() => setActiveTab('pending')}
                >
                    Pending ({pendingUsers.length})
                </button>
                <button 
                    className={activeTab === 'approved' ? 'active' : ''}
                    onClick={() => setActiveTab('approved')}
                >
                    Approved Models
                </button>
                <button 
                    className={activeTab === 'all-users' ? 'active' : ''}
                    onClick={() => setActiveTab('all-users')}
                >
                    All Users
                </button>
                <button 
                    className={activeTab === 'statistics' ? 'active' : ''}
                    onClick={() => setActiveTab('statistics')}
                >
                    Statistics
                </button>
            </div>

            <main>
                {activeTab === 'pending' && (
                    <div className="pending-section">
                        <h2>Awaiting Moderation</h2>
                        {pendingUsers.length === 0 ? (
                            <p>No pending users</p>
                        ) : (
                            pendingUsers.map(user => (
                                <div key={user._id} className="user-card">
                                    <img src={`http://localhost:5000/${user.profilePhoto}`} alt="" />
                                    <div className="user-details">
                                        <h3>{user.nickname}</h3>
                                        <p>Email: {user.email}</p>
                                        <p>Country: {user.country}</p>
                                        <p>Age: {new Date().getFullYear() - new Date(user.birthDate).getFullYear()}</p>
                                    </div>
                                    <div className="passport-photo">
                                        <img src={`http://localhost:5000/${user.passportPhoto}`} alt="Passport" />
                                    </div>
                                    <div className="actions">
                                        <input 
                                            type="number" 
                                            step="0.01"
                                            placeholder="Price per minute"
                                            id={`price-${user._id}`}
                                        />
                                        <button 
                                            onClick={() => {
                                                const price = document.getElementById(`price-${user._id}`).value;
                                                handleApprove(user._id, price);
                                            }}
                                        >
                                            Approve
                                        </button>
                                        <button onClick={() => handleReject(user._id)}>
                                            Reject
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {activeTab === 'approved' && (
                    <div className="approved-section">
                        <h2>Approved Models</h2>
                        {approvedUsers.map(user => (
                            <div key={user._id} className="user-card">
                                <img src={`http://localhost:5000/${user.profilePhoto}`} alt="" />
                                <div className="user-details">
                                    <h3>{user.nickname}</h3>
                                    <p>Current Price: ${user.pricePerMinute}/min</p>
                                    <p>Total Earnings: ${user.totalEarnings?.toFixed(2) || '0.00'}</p>
                                    <p>Status: {user.isOnline ? 'Online' : 'Offline'}</p>
                                </div>
                                <div className="actions">
                                    <input 
                                        type="number" 
                                        step="0.01"
                                        placeholder="New price"
                                        defaultValue={user.pricePerMinute}
                                        id={`price-${user._id}`}
                                    />
                                    <button 
                                        onClick={() => {
                                            const price = document.getElementById(`price-${user._id}`).value;
                                            handleUpdatePrice(user._id, price);
                                        }}
                                    >
                                        Update Price
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {activeTab === 'all-users' && (
                    <div className="all-users-section">
                        <h2>All Users</h2>
                        {allUsers.length === 0 ? (
                            <p>No users found</p>
                        ) : (
                            <div className="users-grid">
                                {allUsers.map(user => (
                                    <div key={user._id} className="user-card">
                                        <img 
                                            src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}/${user.profilePhoto}`} 
                                            alt={user.nickname}
                                            onError={(e) => {
                                                e.target.src = 'https://via.placeholder.com/200';
                                            }}
                                        />
                                        <div className="user-details">
                                            <h3>{user.nickname}</h3>
                                            <p>Email: {user.email}</p>
                                            <p>Gender: {user.gender === 'male' ? 'Male' : 'Female'}</p>
                                            <p>Balance: {Math.floor((user.balance || 0) / 60)} minutes ({user.balance || 0} seconds)</p>
                                            {user.gender === 'female' && (
                                                <>
                                                    <p>Price: ${user.pricePerMinute?.toFixed(2) || '0.00'}/min</p>
                                                    <p>Status: {user.status || 'pending'}</p>
                                                    <p>Total Earnings: ${user.totalEarnings?.toFixed(2) || '0.00'}</p>
                                                </>
                                            )}
                                        </div>
                                        <div className="actions">
                                            <div className="action-group">
                                                <label>Add Balance (minutes):</label>
                                                <input 
                                                    type="number" 
                                                    min="1"
                                                    step="1"
                                                    placeholder="Minutes"
                                                    id={`balance-${user._id}`}
                                                />
                                                <button 
                                                    onClick={() => {
                                                        const minutes = document.getElementById(`balance-${user._id}`).value;
                                                        handleAddBalance(user._id, minutes);
                                                    }}
                                                >
                                                    Add Balance
                                                </button>
                                            </div>
                                            {user.gender === 'female' && (
                                                <div className="action-group">
                                                    <label>Update Price ($/min):</label>
                                                    <input 
                                                        type="number" 
                                                        step="0.01"
                                                        min="0"
                                                        placeholder="Price per minute"
                                                        defaultValue={user.pricePerMinute || 0}
                                                        id={`price-all-${user._id}`}
                                                    />
                                                    <button 
                                                        onClick={() => {
                                                            const price = document.getElementById(`price-all-${user._id}`).value;
                                                            handleUpdatePrice(user._id, price);
                                                        }}
                                                    >
                                                        Update Price
                                                    </button>
                                                </div>
                                            )}
                                            <button 
                                                className="delete-btn"
                                                type="button"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    console.log('🔴 DELETE BUTTON MOUSEDOWN!');
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    console.log('🔴 DELETE BUTTON CLICKED!');
                                                    if (user && user._id && user.nickname) {
                                                        handleDeleteUser(user._id, user.nickname);
                                                    } else {
                                                        console.error('❌ User data missing:', { user });
                                                        notify.error('Error', 'User data missing');
                                                    }
                                                }}
                                                onTouchStart={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    console.log('🔴 DELETE BUTTON TOUCH START!');
                                                    if (user && user._id && user.nickname) {
                                                        handleDeleteUser(user._id, user.nickname);
                                                    }
                                                }}
                                                style={{
                                                    backgroundColor: '#dc3545 !important',
                                                    color: 'white !important',
                                                    border: '2px solid #c82333 !important',
                                                    padding: '10px 16px !important',
                                                    borderRadius: '4px !important',
                                                    cursor: 'pointer !important',
                                                    marginTop: '10px !important',
                                                    width: '100% !important',
                                                    fontSize: '14px !important',
                                                    fontWeight: 'bold !important',
                                                    zIndex: '99999 !important',
                                                    position: 'relative !important',
                                                    pointerEvents: 'auto !important',
                                                    display: 'block !important',
                                                    opacity: '1 !important'
                                                }}
                                            >
                                                🗑️ Delete User
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'statistics' && statistics && (
                    <div className="statistics-section">
                        <h2>Platform Statistics</h2>
                        <div className="stats-grid">
                            <div className="stat-card">
                                <h3>Total Users</h3>
                                <p className="stat-value">{statistics.totalUsers}</p>
                            </div>
                            <div className="stat-card">
                                <h3>Male Users</h3>
                                <p className="stat-value">{statistics.maleUsers}</p>
                            </div>
                            <div className="stat-card">
                                <h3>Female Models</h3>
                                <p className="stat-value">{statistics.femaleUsers}</p>
                            </div>
                            <div className="stat-card">
                                <h3>Pending Approval</h3>
                                <p className="stat-value">{statistics.pendingUsers}</p>
                            </div>
                            <div className="stat-card">
                                <h3>Total Calls</h3>
                                <p className="stat-value">{statistics.totalCalls}</p>
                            </div>
                            <div className="stat-card">
                                <h3>Total Revenue</h3>
                                <p className="stat-value">${statistics.totalRevenue?.toFixed(2) || '0.00'}</p>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

export default AdminDashboard;
